<template>
    <div>
        <h1 class="mt-3 text-lg font-light dark:text-white">Webhooks</h1>

        <div class="mb-3 grid grid-cols-3">
            <div>
                <cards-radio-group
                    v-model="searchType"
                    :options="[
                        { label: 'Timeline', value: 'timeline' },
                        { label: 'Venda', value: 'order' },
                    ]"
                    :disabled="loading"
                />
            </div>
        </div>

        <!-- Componente Timeline -->
        <webhook-timeline v-if="searchType === 'timeline'" />

        <!-- Componente de Venda - Conteúdo original -->
        <div v-else>
            <form @submit.prevent="fetch" class="mb-2 flex">
                <input
                    v-model="search"
                    type="text"
                    name="webhookSearch"
                    id="webhookSearch"
                    class="text-input"
                    required
                    placeholder="Ex.: TPC..., TOC..."
                />

                <submit-button :loading="loading" size="sm" class="ml-2">
                    Buscar
                </submit-button>
            </form>

            <ticto-loading v-if="loading" />
            <div
                v-else
                class="flex flex-col space-y-3 divide-y rounded-lg pb-3 dark:divide-gray-700 dark:bg-slate-800 dark:text-white"
            >
                <div
                    v-for="webhook in webhooks"
                    class="flex items-center px-3 pt-3"
                >
                    <CloudIcon
                        class="text-gray-30 mr-1.5 h-5 w-5"
                        aria-hidden="true"
                    />

                    <span v-if="webhook.commission" class="mr-2">
                        {{ webhook.commission.user.name }} -
                        {{ webhook.commission.user.email }}
                    </span>

                    <span class="max-w-sm truncate" v-tooltip="webhook.webhook_url">
                        {{ webhook.webhook_url }}
                    </span>

                    <NewspaperIcon
                        v-tooltip="'Visualizar Payload'"
                        class="text-gray-305 ml-auto mr-2 h-5 w-5 hover:cursor-pointer"
                        @click.prevent="openModal(webhook.pk)"
                    >
                        Payload
                    </NewspaperIcon>

                    <span
                        class="mr-2 rounded px-2.5 py-1.5 text-sm font-medium"
                        :class="{
                            'bg-green-500 text-white': webhook.status_code === 200,
                            'bg-red-500 text-white': webhook.status_code !== 200,
                        }"
                    >
                        {{ webhook.status_code }}
                    </span>

                    <ClockIcon
                        class="text-gray-30 mr-1.5 h-5 w-5"
                        aria-hidden="true"
                    />

                    <modal
                        :title="`Webhook: ${webhook.webhook_url || 'N/A'}`"
                        size="6xl"
                        :open="modal === webhook.pk"
                        @close="closeModal"
                    >
                                                 <div v-if="loadingEventData" class="p-8 min-w-[500px]">
                             <ticto-loading />
                         </div>

                        <div v-else-if="eventData" class="space-y-6 max-h-[80vh] overflow-y-auto">
                            <!-- Informações Gerais -->
                            <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Informações Gerais</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">ID:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.pk }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Status:</span>
                                        <span class="ml-2 px-2 py-1 rounded text-xs" 
                                              :class="eventData.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                                            {{ eventData.status }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Evento:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.webhook_event || 'N/A' }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Status Code:</span>
                                        <span class="ml-2 px-2 py-1 rounded text-xs"
                                              :class="eventData.status_code === 200 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'">
                                            {{ eventData.status_code }}
                                        </span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Webhook ID:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.webhook_id || 'N/A' }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Commission ID:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.commission_id || 'N/A' }}</span>
                                    </div>
                                    <div class="md:col-span-2">
                                        <span class="font-semibold dark:text-gray-300">URL:</span>
                                        <span class="dark:text-white ml-2 break-all">{{ eventData.webhook_url || 'N/A' }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Criado em:</span>
                                        <span class="dark:text-white ml-2">{{ formatDate(eventData.created_at) }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Atualizado em:</span>
                                        <span class="dark:text-white ml-2">{{ formatDate(eventData.updated_at) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Informações do Pedido -->
                            <div v-if="eventData.payload?.order" class="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Informações do Pedido</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">ID do Pedido:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.order.id }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Hash:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.order.hash }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Valor Pago:</span>
                                        <span class="dark:text-white ml-2">R$ {{ (eventData.payload.order.paid_amount / 100).toFixed(2) }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Parcelas:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.order.installments }}x</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Data do Pedido:</span>
                                        <span class="dark:text-white ml-2">{{ formatDate(eventData.payload.order.order_date) }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Hash da Transação:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.order.transaction_hash }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Informações do Cliente -->
                            <div v-if="eventData.payload?.customer" class="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Informações do Cliente</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Nome:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.name }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Email:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.email }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Código:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.code }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Telefone:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.phone?.ddi }}{{ eventData.payload.customer.phone?.ddd }}{{ eventData.payload.customer.phone?.number }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">CPF:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.cpf || 'N/A' }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">País:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.customer.country?.name || 'N/A' }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Informações do Produto -->
                            <div v-if="eventData.payload?.item" class="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Informações do Produto</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Produto:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.item.product_name }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Oferta:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.item.offer_name }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Valor:</span>
                                        <span class="dark:text-white ml-2">R$ {{ (eventData.payload.item.amount / 100).toFixed(2) }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Quantidade:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.item.quantity }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Código da Oferta:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.item.offer_code }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Prazo Reembolso:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.item.refund_deadline }} dias</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Informações do Produtor -->
                            <div v-if="eventData.payload?.producer" class="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Informações do Produtor</h3>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Nome:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.producer.name }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Email:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.producer.email }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Documento:</span>
                                        <span class="dark:text-white ml-2">{{ eventData.payload.producer.document }}</span>
                                    </div>
                                    <div>
                                        <span class="font-semibold dark:text-gray-300">Valor Comissão:</span>
                                        <span class="dark:text-white ml-2">R$ {{ (eventData.payload.producer.amount / 100).toFixed(2) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Response Body -->
                            <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                <h3 class="text-lg font-semibold dark:text-white mb-3">Response</h3>
                                <div class="dark:text-white bg-gray-100 dark:bg-gray-900 p-3 rounded max-h-64 overflow-y-auto">
                                    <pre class="text-xs whitespace-pre-wrap">{{ eventData.response_body }}</pre>
                                </div>
                            </div>

                            <!-- Payload Completo (Collapsed) -->
                            <div class="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                                <button @click="showFullPayload = !showFullPayload" 
                                        class="flex items-center justify-between w-full text-left">
                                    <h3 class="text-lg font-semibold dark:text-white">Payload Completo</h3>
                                    <svg :class="{ 'rotate-180': showFullPayload }" 
                                         class="w-5 h-5 transform transition-transform dark:text-white" 
                                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>
                                <div v-show="showFullPayload" class="mt-3">
                                    <div class="dark:text-white bg-gray-100 dark:bg-gray-900 p-3 rounded max-h-96 overflow-y-auto">
                                        <pre class="text-xs whitespace-pre-wrap">{{ JSON.stringify(eventData.payload, null, 2) }}</pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div v-else-if="eventError" class="text-center py-8">
                            <p class="dark:text-white text-red-500">{{ eventError }}</p>
                        </div>
                    </modal>

                    {{ formatDate(webhook.created_at) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CardsRadioGroup from "../../components/forms/CardsRadioGroup.vue";
import SubmitButton from "../../components/forms/SubmitButton.vue";
import TictoLoading from "../../components/layouts/TictoLoading.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";
import { ClockIcon, CloudIcon, NewspaperIcon } from "@heroicons/vue/24/outline";
import Modal from "../../components/layouts/Modal.vue";
import WebhookTimeline from "./WebhookTimeline.vue";

export default {
    components: {
        Modal,
        TictoLoading,
        SubmitButton,
        CardsRadioGroup,
        ClockIcon,
        CloudIcon,
        NewspaperIcon,
        WebhookTimeline
    },
    mixins: [HelperMixin],
    data() {
        return {
            loading: false,
            searchType: "timeline",
            search: "",
            webhooks: [],
            modal: false,
            loadingEventData: false,
            eventData: null,
            eventError: null,
            showFullPayload: false,
        };
    },

    methods: {
        fetch() {
            this.loading = true;
            axios
                .get("/api/webhook", {
                    params: {
                        type: this.searchType,
                        search: this.search,
                    },
                })
                .then(({ data }) => {
                    this.webhooks = data;
                })
                .catch(({ response }) => {
                    if ([404, 400].includes(response.status)) {
                        this.$notify(
                            {
                                group: "error",
                                title: "Error",
                                text: response.data.message,
                            },
                            4000,
                        );
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        async openModal(eventId) {
            this.modal = eventId;
            this.loadingEventData = true;
            this.eventData = null;
            this.eventError = null;

            try {
                const { data } = await axios.get(`/api/webhook/event/${eventId}`);
                this.eventData = data;
            } catch (error) {
                this.eventError = error.response?.data?.message || 'Erro ao carregar dados do evento';
                console.error('Erro ao buscar evento:', error);
            } finally {
                this.loadingEventData = false;
            }
        },
        closeModal() {
            this.modal = false;
            this.eventData = null;
            this.eventError = null;
            this.showFullPayload = false;
        },
    },
};
</script>
